import { AsyncPipe, DecimalPipe, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PRODUCT_PLACEHOLDER } from 'src/app/app.settings';
import { Product, ProductLine, ProductPackageOption } from 'src/app/models';
import { buttonIcons, symbolIcons } from 'src/app/modules/icon.module';
import { AuthService } from 'src/app/services/auth';
import { BasketQuery } from 'src/app/services/basket/basket.query';
import { BasketService } from 'src/app/services/basket/basket.service';
import { ContentService } from 'src/app/services/content/content.service';
import { ProductService } from 'src/app/services/product';
import { IconButtonComponent, TextButtonComponent } from 'src/app/shared/components';
import { CounterComponent } from 'src/app/shared/components/product/counter/counter.component';
import { PriceFieldComponent } from 'src/app/shared/components/product/price-field/price-field.component';
import { environment } from 'src/environments/environment';
import { DisplayCurrencyPipe } from '../../../pipes/display-currency.pipe';
import { ProductModulePipe } from '../../../pipes/module.pipe';
import { PriceSumPipe } from '../../../pipes/price-sum.pipe';
import { PrimaryProductImagePipe } from '../../../pipes/primary-product-image.pipe';
import { ContentSection } from '../../content-section';

@Component({
  selector: 'app-basket',
  templateUrl: './basket.component.html',
  styleUrls: ['./basket.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    IconButtonComponent,
    AsyncPipe,
    DecimalPipe,
    DisplayCurrencyPipe,
    TextButtonComponent,
    RouterLink,
    PrimaryProductImagePipe,
    ProductModulePipe,
    PriceFieldComponent,
    PriceSumPipe,
    CounterComponent,
    MatIconModule,
    NgOptimizedImage,
  ],
})
export class BasketComponent extends ContentSection implements OnInit {
  private basketService = inject(BasketService);
  private basketQuery = inject(BasketQuery);
  private authService = inject(AuthService);
  private productService = inject(ProductService);

  closeIcon = buttonIcons.bigCross;
  basketOverviewUrl = `/${environment.urls.basket}`;
  locale = environment.i18n.language;
  currency = environment.i18n.currency;
  placeholder = PRODUCT_PLACEHOLDER;
  multiplyIcon = symbolIcons.delete;

  @Input() show = false;

  priceHeader: string;
  basketButtonText: string;
  debitorWarningLabel: string;
  discountLabel: string;
  simpleOverviewItems: boolean;

  price$: Observable<number>;
  discount$: Observable<number>;
  productLines$: Observable<ProductLine[]>;
  isLoading$: Observable<boolean>;

  loggedIn: Observable<boolean>;

  constructor() {
    const contentService = inject(ContentService);
    super(contentService, 'basket');

    this.basketButtonText = this.content.get('goToBasketButtonText');

    const sharedContent = this.contentService.getSharedContent();
    this.priceHeader = sharedContent.get('totalPriceLabelText');

    this.debitorWarningLabel = sharedContent.get('debitorWarningText');
    this.discountLabel = this.content.get('discountLabelText');
    this.simpleOverviewItems = this.content.get('simpleOverviewItems');
  }

  ngOnInit() {
    this.price$ = this.basketQuery.selectTotal$;
    this.discount$ = this.basketQuery.selectDiscount$;
    this.productLines$ = this.basketQuery.selectProductLines$;
    this.isLoading$ = this.basketQuery.selectLoading();

    this.loggedIn = this.authService.member.pipe(map((member) => !!member));
  }

  getProduct(productLine: ProductLine): Observable<Product> {
    return this.basketService.getProductObservable(productLine);
  }

  addProduct(product: Product, quantity: number, packageOptions: ProductPackageOption[]) {
    this.basketService.addProduct(product, quantity, packageOptions);
  }

  removeProduct(product: Product, quantity = 1, packageOptions: ProductPackageOption[]) {
    this.basketService.removeProduct(product, quantity, packageOptions);
  }

  deleteProduct(product: Product) {
    this.basketService.deleteProduct(product);
  }

  createProductUrl(product: Product) {
    return this.productService.createProductUrl(product);
  }

  close() {
    this.basketService.setOverlay(false);
  }
}
